import { inject, Injectable } from '@angular/core';
import {
  AssistantApiService,
  AssistantInterface,
  AssistantType,
  UpsertAssistantResponse,
} from '@vendasta/ai-assistants';
import { ACCOUNT_GROUP_ID_TOKEN, AI_DEFAULT_WORKFORCE_TOKEN, PARTNER_ID_TOKEN } from '../../../core/tokens';
import { AiAssistant } from '../../../core/interfaces/ai-assistant.interface';
import { getNamespace } from '../../../core/services/ai-assistant-utils';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, EMPTY, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { AiAssistantService } from '../../../core/services/ai-assistant.service';

@Injectable()
export class AiAssistantFormV2Service {
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);
  private readonly defaultWorkforce$ = inject(AI_DEFAULT_WORKFORCE_TOKEN);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly assistantApiService = inject(AssistantApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackbarService = inject(SnackbarService);

  private readonly assistantId$: Observable<string>;
  readonly aiAssistant$: Observable<AiAssistant | null>;

  constructor() {
    this.assistantId$ = this.route.paramMap.pipe(
      map((params) => params.get('assistantId') || ''),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.aiAssistant$ = combineLatest([
      this.assistantId$,
      this.partnerId$,
      this.accountGroupId$,
      this.defaultWorkforce$,
    ]).pipe(
      switchMap(([assistantId, partnerId, accountGroupId, defaultWorkforce]): Observable<AiAssistant | null> => {
        // If we're creating a new assistant
        if (!assistantId) {
          const namespace = getNamespace(accountGroupId, partnerId);
          if (!namespace) {
            throw of(null);
          }
          return of({
            assistant: {
              id: '',
              name: 'Custom Assistant',
              type: AssistantType.ASSISTANT_TYPE_CUSTOM,
              avatarUrl: '',
              namespace: namespace,
            },
          });
        }

        return this.aiAssistantService.getAssistant(partnerId, accountGroupId, assistantId).pipe(
          map((assistant) => {
            return this.aiAssistantService.hydrateAssistantWithDefaultInfo(assistant, defaultWorkforce);
          }),
          catchError((err) => {
            if (err.status === 404) {
              return this.aiAssistantService.getDefaultAssistant(partnerId, accountGroupId, assistantId).pipe(
                map((assistant) => assistant),
                catchError(() => {
                  this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ASSISTANT_NOT_FOUND');
                  return EMPTY;
                }),
              );
            } else {
              this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.ERROR_FETCHING_ASSISTANT');
              return EMPTY;
            }
          }),
        );
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  upsertAssistant(assistant: AssistantInterface, applyDefaults = false): Observable<UpsertAssistantResponse> {
    return this.assistantApiService.upsertAssistant({
      assistant: assistant,
      options: {
        applyDefaults: applyDefaults,
      },
    });
  }
}
